<template>
  <div class="">
    <!-- Completed certificate: compact card (Figma node 162:10171) -->
    <div v-if="isCompleted" class="completed-cert">
      <div class="completed-cert-main">
        <div class="completed-cert-thumb">
          <v-img :src="certificateImageUrl" alt="Certificate Image" cover width="114" height="80" />
        </div>
        <div class="completed-cert-text">
          <span class="completed-cert-badge">COMPLETED</span>
          <h3 class="completed-cert-title">{{ certificate.display_label || certificate.credential_name }}</h3>
          <p v-if="completedOnDate" class="completed-cert-date">Completed on {{ completedOnDate }}</p>
        </div>
      </div>
      <div class="completed-cert-actions">
        <button v-if="certificate.credential_url" class="completed-cert-view-btn" @click.stop="openCredential">
          View certificate
        </button>
      </div>
    </div>

    <!-- Registered / in-progress certificate: full card with progress + required courses -->
    <template v-else>
      <!-- Certificate Title and Image -->
      <v-row no-gutters class="mb-4" style="min-height: 160px">
        <!-- Image Column -->
        <v-col cols="12" sm="4" md="4" lg="4" xl="4" class="d-flex align-stretch">
          <v-img
            :src="certificateImageUrl"
            alt="Certificate Image"
            class="rounded w-100"
            cover
            style="min-height: 140px"
          />
        </v-col>

        <!-- Content Column -->
        <v-col cols="12" sm="8" md="8" lg="8" xl="8" class="ps-sm-4 pt-3 pt-sm-0">
          <!-- Card Header with REGISTERED badge and menu icon -->
          <v-card-title class="d-flex justify-space-between align-center pa-0">
            <!-- Status Badge -->
            <div class="d-flex align-center">
              <span
                class="text-caption font-weight-bold mr-3"
                :style="{
                  backgroundColor: isCompleted ? '#e8f5e9' : '#ffeebe',
                  color: isCompleted ? '#2e7d32' : '#000',
                  letterSpacing: '0.5px',
                  padding: '3px 16px',
                  borderRadius: '4px',
                  display: 'inline-block'
                }"
              >
                {{ isCompleted ? 'COMPLETED' : 'REGISTERED' }}
              </span>
            </div>

            <!-- Menu with Unregister Option -->
            <v-menu location="bottom end">
              <template v-slot:activator="{ props }">
                <v-btn style="margin-right: 0 !important" icon variant="text" size="x-small" v-bind="props">
                  <v-icon size="large" color="#747474">mdi-dots-horizontal</v-icon>
                </v-btn>
              </template>
              <v-card class="rounded-lg" elevation="0" style="border: 1px solid #bfbfbf">
                <v-list density="compact">
                  <v-list-item @click="handleUnregister">
                    <v-list-item-title class="text-body-2"> Remove Certificate </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </v-card-title>

          <!-- Certificate Title -->
          <h3 class="font-weight-bold mb-1 mt-4 mt-md-0" style="font-size: 20px; line-height: 1.3">
            {{ certificate.display_label || certificate.credential_name }}
          </h3>

          <!-- Certificate Progress Bar / View Certificate button -->
          <div v-if="certificateProgress.total > 0" class="mt-3">
            <!-- When 100% complete and POK credential is ready, show the button instead of the bar -->
            <v-btn
              v-if="isCompleted && certificate.credential_url"
              variant="outlined"
              style="
                text-transform: none;
                font-weight: 600;
                border-color: #8c1d40;
                color: #8c1d40;
                border-radius: 8px;
                font-size: 14px;
                padding: 0 20px;
                height: 36px;
              "
              @click.stop="openCredential"
            >
              View certificate
            </v-btn>

            <!-- In-progress: keep the normal progress bar -->
            <ProgressBar
              v-else
              :transcripted="0"
              :waiting="certificateProgress.completed"
              :in-progress="0"
              :not-started="certificateProgress.total - certificateProgress.completed"
              :total-credits="certificateProgress.total"
              :show-legend="false"
              unit="courses"
            />
          </div>
        </v-col>
      </v-row>

      <!-- Divider and Courses Section (hidden when showHeaderOnly is true) -->
      <transition name="courses-expand">
        <div v-if="!showHeaderOnly" class="courses-section">
          <!-- Divider -->
          <v-divider class="my-4" style="border-color: #bfbfbf; opacity: 1" />

          <!-- Required Courses Section -->
          <div v-if="enrichedCourses && enrichedCourses.length > 0">
            <h4 class="font-weight-bold mb-3" style="font-size: 18px">Required courses</h4>

            <!-- Course Groups -->
            <div class="d-flex flex-column gap-3">
              <template v-for="(courseGroup, groupIndex) in enrichedCourses" :key="groupIndex">
                <!-- Single Course (AND requirement) -->
                <CourseCardCompact
                  v-if="courseGroup.length === 1"
                  :course="courseGroup[0]"
                  :is-last-course="true"
                  :variant="getCourseVariant(courseGroup[0])"
                  @add-course="handleAddCourse"
                  @button-action="$emit('button-action', $event)"
                  @reload="$emit('reload')"
                  @reloadDashboardOrPathway="$emit('reloadDashboardOrPathway', $event)"
                />

                <!-- Multiple Courses (OR requirement) - Collapsible -->
                <CourseOrGroup v-else :courses="courseGroup">
                  <CourseCardCompact
                    v-for="(course, courseIndex) in courseGroup"
                    :key="course.coursePrefix"
                    :course="course"
                    :is-last-course="courseIndex === courseGroup.length - 1"
                    :variant="getCourseVariant(course)"
                    @add-course="handleAddCourse"
                    @button-action="$emit('button-action', $event)"
                    @reload="$emit('reload')"
                    @reloadDashboardOrPathway="$emit('reloadDashboardOrPathway', $event)"
                  />
                </CourseOrGroup>
              </template>
            </div>
          </div>
        </div>
      </transition>
    </template>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { DEFAULT_CERTIFICATE_IMAGE, isCertificateEarned } from '@/constants/certificates';
import { useCheckoutStore } from '@/stores/checkout';
import { resolveOfferingCanvasCourseId } from '@/utils/ga4Items';
import { useUserStore } from '@/stores/user';
import CourseCardCompact from '@/components/courses/CourseCardCompact.vue';
import CourseOrGroup from '@/components/courses/CourseOrGroup.vue';
import ProgressBar from '@/components/ui/ProgressBar.vue';
import { ENROLLMENT_TYPE_LABELS, PREPAID_ENROLLMENT_BENEFIT_TYPES } from '@/constants/checkout';

export default defineComponent({
  name: 'EnrolledCertificateCard',

  components: {
    CourseCardCompact,
    CourseOrGroup,
    ProgressBar
  },

  props: {
    certificate: {
      type: Object,
      required: true
    },
    showHeaderOnly: {
      type: Boolean,
      default: false
    }
  },

  emits: ['unregister', 'button-action', 'reload', 'reloadDashboardOrPathway'],

  data() {
    return {
      defaultImage: DEFAULT_CERTIFICATE_IMAGE
    };
  },

  computed: {
    certificateImageUrl() {
      return this.certificate.image_url || this.defaultImage;
    },

    /**
     * Get parsed course requirements from backend
     * Backend returns simple nested array format:
     * [
     *   ['CIS 194', 'CIS 194x'],  // Step 1: Choose one (OR)
     *   ['CIS 394y'],              // Step 2: Required
     *   ['CIS 394z']               // Step 3: Required
     * ]
     * Rule: Items within a sub-array are OR'd, sub-arrays are AND'd in strict order
     */
    courseRequirements() {
      return this.certificate.parsed_courses || [];
    },

    /**
     * Get enriched courses with full course data and button states
     * Structure: Array<Array<EnrichedCourse>>
     */
    enrichedCourses() {
      return this.certificate.enriched_courses || [];
    },

    certificateProgress() {
      return this.certificate.progress || { completed: 0, total: 0 };
    },

    isCompleted() {
      return isCertificateEarned(this.certificate);
    },

    /**
     * "Completed on MM/DD/YYYY" date sourced from the merged POK credential's
     * earned_at. Returns null when no valid date is available (line is hidden).
     */
    completedOnDate() {
      const raw = this.certificate.earned_at;
      if (!raw) return null;
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return null;
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${mm}/${dd}/${d.getFullYear()}`;
    }
  },

  methods: {
    getCourseVariant(course) {
      const section = course.section?.name;
      if (section === 'Transcripted Courses') return 'history';
      if (section === 'Past Courses') return 'past';
      return 'schedule';
    },

    handleUnregister() {
      this.$emit('unregister', this.certificate);
    },

    openCredential() {
      try {
        const parsed = new URL(this.certificate.credential_url, window.location.origin);
        if (!['http:', 'https:'].includes(parsed.protocol)) return;
        window.open(parsed.toString(), '_blank', 'noopener,noreferrer');
      } catch {
        // ignore malformed URLs
      }
    },

    /**
     * Handle add-course event from CourseCardCompact dropdown selection.
     * Populates checkout store and navigates to checkout — mirrors the
     * deeplink flow in Dashboard.vue (enrollmentType: 'new').
     */
    handleAddCourse(eventData) {
      const checkoutStore = useCheckoutStore();
      const course = eventData.course;
      const selectedOfferingId = String(eventData.courseOfferingId || '');
      const selectedOffering = course.offerings?.find((o) => String(o.offeringId) === selectedOfferingId);
      const openEdxCourseId =
        selectedOffering?.openEdxCourseId || course.offering?.openEdxCourseId || course.lms?.openEdxCourseId || '';
      const payment = course.payment || {};
      const benefitType = String(useUserStore().apis?.learnerData?._benefitType || '');
      const multiEnrollmentType = ENROLLMENT_TYPE_LABELS[payment.enrollmentType] || '';
      // Mirrors the checkout's own prepaid gate (isPrepaidCourseAmount) so the card never
      // claims a prepaid checkout the checkout will refuse; a LearnerPay course can be
      // benefitsEligible with a real price, so an explicit $0 is required too.
      const isPrepaid =
        payment.benefitsEligible === true &&
        payment.creditEligibilityPrice === 0 &&
        PREPAID_ENROLLMENT_BENEFIT_TYPES.includes(benefitType) &&
        multiEnrollmentType !== 'LearnerPay';

      checkoutStore.setCheckoutData({
        title: course.courseName || '',
        coursePrefix: course.coursePrefix || '',
        coursePrefixDisplayLabel: course.coursePrefixDisplayLabel || course.coursePrefix || '',
        courseStartDate: eventData.courseStartDate || '',
        courseEndDate: eventData.courseEndDate || selectedOffering?.endDate || course.offering?.endDate || '',
        courseType: course.courseType || '',
        credits: course.credits || 0,
        courseOfferingId: selectedOfferingId,
        courseOfferingName: openEdxCourseId,
        sessionName: selectedOffering?.sessionName || '',
        enrollmentId: '0',
        enrollmentType: 'new',
        buttonLabel: 'Access Course',
        // A covered course checks out at $0, where the Stripe form never mounts — only the
        // prepaid path ('Enroll and pay' + a covered benefit type) completes the enrollment.
        buttonAction: isPrepaid ? 'Enroll and pay' : 'Access Course',
        creditEligibilityPrice: payment.creditEligibilityPrice || 0,
        benefitsEligible: payment.benefitsEligible || false,
        benefitType,
        multiEnrollmentType,
        canvasEnrollmentId: 0,
        canvasUserId: course.lms?.canvasUserId || null,
        canvasCourseId: resolveOfferingCanvasCourseId(selectedOffering || course.offering, course.lms),
        from: 'certificates',
        courseInfo: { _openEdxCourseId: openEdxCourseId }
      });

      this.$router.push({ name: 'courseCheckout' });
    }
  }
});
</script>

<style scoped>
.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

/* Smooth transition for courses section */
.courses-expand-enter-active,
.courses-expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.courses-expand-enter-from,
.courses-expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.courses-expand-enter-to,
.courses-expand-leave-from {
  max-height: 5000px;
  opacity: 1;
}

/* ── Completed certificate compact card (Figma node 162:10171) ─────────────── */
.completed-cert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
}

.completed-cert-main {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  flex: 1 0 0;
  min-width: 0;
}

.completed-cert-thumb {
  flex-shrink: 0;
  width: 114px;
  height: 80px;
  border-radius: 4px;
  overflow: hidden;
}

.completed-cert-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.completed-cert-badge {
  align-self: flex-start;
  padding: 2px 10px;
  background: rgba(254, 198, 39, 0.3);
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  color: #191919;
}

.completed-cert-title {
  margin: 0;
  font-family: Arial, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: -0.03em;
  color: #191919;
  text-decoration: underline solid;
  text-underline-offset: -100%;
  text-decoration-thickness: 150%;
}

.completed-cert-date {
  margin: 0;
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #747474;
}

.completed-cert-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.completed-cert-view-btn {
  appearance: none;
  cursor: pointer;
  min-width: 112px;
  height: 42px;
  padding: 0 32px;
  background: #ffffff;
  border: 1px solid #000000;
  border-radius: 100px;
  font-family: Arial, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: #191919;
  white-space: nowrap;
  transition: background 0.2s ease;
}
.completed-cert-view-btn:hover {
  background: #f5f5f5;
}

@media (max-width: 600px) {
  .completed-cert {
    flex-direction: column;
    align-items: flex-start;
  }
  .completed-cert-actions {
    width: 100%;
  }
  .completed-cert-view-btn {
    flex: 1;
  }
}
</style>
